import { Form, Head, usePage } from "@inertiajs/react"
import type { ReactNode } from "react"
import MatchLayout from "@/layouts/match-layout"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

interface UserData {
  id: number
  name: string
  email: string
  role: string
  player_id: number | null
}

interface PlayerData {
  id: number
  name: string
  team: string
}

export default function StaffUsersEdit() {
  const { user, players } = usePage().props as unknown as { user: UserData; players: PlayerData[] }

  return (
    <>
      <Head title={`Edit: ${user.name}`} />
      <div className="border-b bg-card">
        <div className="mx-auto max-w-2xl px-4 py-6">
          <h1 className="text-2xl font-bold tracking-tight">Edit User: {user.name}</h1>
        </div>
      </div>

      <div className="mx-auto max-w-2xl px-4 py-6">
        <Form
          method="put"
          action={`/staff/users/${user.id}`}
          className="flex flex-col gap-6"
        >
          {({ processing, errors }) => (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Account Details</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">Name</Label>
                    <Input id="name" name="name" required defaultValue={user.name} />
                    <InputError messages={errors.name} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" name="email" type="email" required defaultValue={user.email} />
                    <InputError messages={errors.email} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="password">New Password (leave blank to keep current)</Label>
                    <Input id="password" name="password" type="password" placeholder="Min 12 characters" />
                    <InputError messages={errors.password} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="password_confirmation">Confirm New Password</Label>
                    <Input id="password_confirmation" name="password_confirmation" type="password" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Role & Player Link</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="role">Role</Label>
                    <select
                      id="role"
                      name="role"
                      required
                      defaultValue={user.role}
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      <option value="admin">Admin</option>
                      <option value="coach">Coach</option>
                      <option value="player">Player</option>
                    </select>
                    <InputError messages={errors.role} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="player_id">Linked Player</Label>
                    <select
                      id="player_id"
                      name="player_id"
                      defaultValue={user.player_id || ""}
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      <option value="">— None —</option>
                      {players.map(p => (
                        <option key={p.id} value={p.id}>{p.name} ({p.team})</option>
                      ))}
                    </select>
                    <InputError messages={errors.player_id} />
                  </div>
                </CardContent>
              </Card>

              <Button type="submit" disabled={processing}>
                Update User
              </Button>
            </>
          )}
        </Form>
      </div>
    </>
  )
}

StaffUsersEdit.layout = (page: ReactNode) => <MatchLayout>{page}</MatchLayout>
